import { Preferences } from "@capacitor/preferences";
import type { CalcResult } from "@core/mbti/scoring";
import { apiFetch } from "./api";
import { queryClient } from "./queryClient";

const PENDING_MBTI_KEY = "sotda_pending_mbti_diagnosis_v1";

export interface MbtiRecordPayload {
  type_code: string;
  radar: CalcResult["radar"];
  mbti_strength: CalcResult["mbtiStrength"];
  answers: Record<number, number>;
  sub_tags: string[];
  boundary_axes: string[];
  source: "app" | "web";
}

export interface PendingMbtiDiagnosis {
  version: 1;
  savedAt: number;
  result: CalcResult;
  payload: MbtiRecordPayload;
}

export function buildMbtiRecordPayload(
  result: CalcResult,
  answers: Record<number, number>,
): MbtiRecordPayload {
  return {
    type_code: result.code,
    radar: result.radar,
    mbti_strength: result.mbtiStrength,
    answers,
    sub_tags: result.tags || [],
    boundary_axes: (result.boundaries || []).map((boundary) => boundary.key),
    source: "app",
  };
}

export async function savePendingMbtiDiagnosis(
  result: CalcResult,
  answers: Record<number, number>,
): Promise<PendingMbtiDiagnosis> {
  const pending: PendingMbtiDiagnosis = {
    version: 1,
    savedAt: Date.now(),
    result,
    payload: buildMbtiRecordPayload(result, answers),
  };
  await Preferences.set({ key: PENDING_MBTI_KEY, value: JSON.stringify(pending) });
  return pending;
}

export async function readPendingMbtiDiagnosis(): Promise<PendingMbtiDiagnosis | null> {
  const { value } = await Preferences.get({ key: PENDING_MBTI_KEY });
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as PendingMbtiDiagnosis;
    if (parsed.version !== 1 || !parsed.result?.code || !parsed.payload?.type_code) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** 서버 저장이 확인된 뒤에만 호출한다(N-34: 로그인 경로도 성공 전에는 사본을 지우지 않는다). */
export async function clearPendingMbtiDiagnosis(): Promise<void> {
  await Preferences.remove({ key: PENDING_MBTI_KEY });
}

let flushPromise: Promise<boolean> | null = null;

/** 로그인 직후 로컬 진단을 서버에 1회 저장하고, 성공한 경우에만 로컬 사본을 지운다. */
export function flushPendingMbtiDiagnosis(): Promise<boolean> {
  if (flushPromise) return flushPromise;
  flushPromise = (async () => {
    const pending = await readPendingMbtiDiagnosis();
    if (!pending) return false;
    await apiFetch("/api/mbti/record", { method: "POST", body: pending.payload });
    await Preferences.remove({ key: PENDING_MBTI_KEY });
    // D-10 동계열: 로그인 직후 이 경로로 저장돼도 /mbti·홈이 옛 "결과 없음" 캐시를 쓰면
    // 진단 전 화면이 그대로 남는다. 저장 성공 시점에 캐시를 버린다.
    queryClient.invalidateQueries({ queryKey: ["mobile", "mbti"] });
    queryClient.invalidateQueries({ queryKey: ["mobile", "home"] });
    queryClient.invalidateQueries({ queryKey: ["me"] });
    return true;
  })().finally(() => {
    flushPromise = null;
  });
  return flushPromise;
}
